/**
 * Project Dashboard — /projects/:id
 * Progressive disclosure: action hero → journey phases → simplified tabs.
 * AI nudges drive the hero. Tabs lazy-load on click.
 */
import { useState, useEffect, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useToast } from "../components/shared/Toast.tsx";
import { FileText, Shield, ShieldCheck, ClipboardList, ChevronRight, List, Map, FileOutput, Download, Trash2, RotateCcw, CheckCircle, ArrowRight, AlertTriangle, ArrowUpDown, Filter, Eye, Sparkles } from "lucide-react";
import { api } from "../lib/api.ts";
import type { Project } from "../types/project.ts";
import { HealthRing } from "../components/shared/HealthRing.tsx";
import { StatusPill } from "../components/shared/StatusPill.tsx";
import { FieldCard } from "../components/fields/FieldCard.tsx";
import { WorkplanView } from "../components/workplan/WorkplanView.tsx";
import { BatchUploadFlow } from "../components/documents/BatchUploadFlow.tsx";
import { TemplateList } from "../components/templates/TemplateList.tsx";
import { FIELD_CATEGORIES, COMPLIANCE_STATUS_CONFIG, SHOW_BANKABILITY, visibleNudges } from "../lib/constants.ts";
import { ErrorMessage } from "../components/shared/ErrorMessage.tsx";
import { CloudDriveConnect } from "../components/cloud/CloudDriveConnect.tsx";
import { DocumentPreviewModal } from "../components/documents/DocumentPreviewModal.tsx";
import { Tooltip } from "../components/shared/Tooltip.tsx";
import { Tabs, tabPanelProps } from "../components/shared/Tabs.tsx";
import { ConfirmDialog } from "../components/shared/Modal.tsx";
import { EvaluationPanel } from "../components/evaluation/EvaluationPanel.tsx";

export function ProjectDashboard() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState<Project | null>(null);
  const [fields, setFields] = useState<any[]>([]);
  const [licenses, setLicenses] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [complianceSummary, setComplianceSummary] = useState<any>(null);
  const [complianceItems, setComplianceItems] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "licenses");
  const focusFieldId = searchParams.get("field");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [evaluation, setEvaluation] = useState<any>(null);
  const [evaluationLoaded, setEvaluationLoaded] = useState(false);
  const heroContentRef = useRef<HTMLDivElement>(null);
  const [heroHeight, setHeroHeight] = useState<number | null>(null);
  const prevNudgeIdsRef = useRef<Set<string>>(new Set());
  const { showToast } = useToast();

  function loadData() {
    if (!id) return;
    Promise.all([
      api.getProject(id),
      api.getProjectFields(id),
      api.getProjectLicenses(id),
      api.getProjectAlerts(id),
      api.getProjectCompliance(id).catch(() => ({ items: [], summary: null })),
    ]).then(([proj, flds, lics, alts, compliance]) => {
      setProject(proj);
      setFields(flds);
      setLicenses(lics);
      setAlerts(alts);
      setComplianceSummary(compliance.summary);
      setComplianceItems(compliance.items || []);
    }).catch((err) => {
      console.error("Failed to load project:", err);
      setError(err instanceof Error ? err.message : "Failed to load project");
    }).finally(() => {
      setLoading(false);
    });
  }

  useEffect(() => { loadData(); }, [id]);

  // Sync activeTab when URL param changes (e.g., user clicks a nudge with ?tab=fields)
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab && urlTab !== activeTab) setActiveTab(urlTab);
  }, [searchParams]);

  // Lazy-load evaluation nudges for hero action cards
  function refreshEvaluation() {
    if (!id) return;
    setEvaluationLoaded(false);
    api.getProjectEvaluation(id)
      .then(setEvaluation)
      .catch(() => {})
      .finally(() => setEvaluationLoaded(true));
  }

  useEffect(() => { refreshEvaluation(); }, [id]);

  // Measure hero content height for smooth transitions
  useEffect(() => {
    if (!heroContentRef.current) return;
    const measure = () => {
      if (heroContentRef.current) {
        setHeroHeight(heroContentRef.current.scrollHeight);
      }
    };
    measure();
    // Re-measure on window resize too
    const ro = new ResizeObserver(measure);
    ro.observe(heroContentRef.current);
    return () => ro.disconnect();
  }, [evaluation, evaluationLoaded]);

  // Detect new nudges and toast the user when fresh actions arrive
  useEffect(() => {
    if (!evaluationLoaded) return;
    const currentNudges = visibleNudges(evaluation?.nudges);
    const currentIds = new Set<string>(currentNudges.map((n: any) => n.id));
    const prev = prevNudgeIdsRef.current;
    // Skip the very first load (when prev is empty)
    if (prev.size > 0) {
      const newOnes = currentNudges.filter((n: any) => !prev.has(n.id));
      if (newOnes.length > 0) {
        const first = newOnes[0];
        showToast(
          newOnes.length === 1
            ? `New action: ${first.title}`
            : `${newOnes.length} new actions to review`,
          "info",
        );
      }
    }
    prevNudgeIdsRef.current = currentIds;
  }, [evaluation, evaluationLoaded]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-surface-tertiary rounded w-1/4" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-16 bg-surface-tertiary rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6">
        <ErrorMessage message={error} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  if (!project) {
    return <div className="max-w-5xl mx-auto px-4 py-6 text-text-secondary">Project not found.</div>;
  }

  const fieldsComplete = fields.filter(f => f.value != null && f.value !== "").length;
  const fieldsTotal = fields.length;
  const readiness = fieldsTotal > 0 ? Math.round((fieldsComplete / fieldsTotal) * 100) : 0;

  // Counts follow the engine's verdicts: excluded (not_applicable) and
  // corpus-retired rows don't inflate the headline numbers — an isolated
  // mini-grid must not read "48 licences" when 15 of those are the engine
  // saying "not you". In-flight rows always count (mirror of the display
  // partition in LicensesTab).
  const bindingCount = licenses.filter(
    l => !["not_started", "preparing", "blocked"].includes(l.status)
      || (l.applicability !== "not_applicable" && !l.retired_reason),
  ).length;

  // Compute journey phase from project state
  const licensesApproved = licenses.filter(l => l.status === "approved").length;
  const phase = readiness < 40 ? 0 : readiness < 70 ? 1 : licensesApproved < bindingCount ? 2 : 3;
  const phases = [
    { label: "Data Collection", description: "Fill fields and upload documents" },
    { label: "License Preparation", description: "Complete applications and templates" },
    { label: "Submission & Tracking", description: "Submit and track license applications" },
    { label: "Operations", description: "Monitor compliance and renewals" },
  ];

  // Top action cards from evaluation nudges (loaded lazily, hook at top of
  // component). visibleNudges is the ONE filter — hero, tab badge and the
  // evaluation panel must never disagree about which nudges exist (#16).
  const topActions = visibleNudges(evaluation?.nudges).slice(0, 5);

  // Phase-aware progress summary — over binding rows only, so these numbers
  // can never exceed licensesTotal.
  const bindingRows = licenses.filter(
    l => !["not_started", "preparing", "blocked"].includes(l.status)
      || (l.applicability !== "not_applicable" && !l.retired_reason),
  );
  const licensesNotStarted = bindingRows.filter(l => l.status === "not_started" || l.status === "blocked").length;
  const licensesPreparing = bindingRows.filter(l => l.status === "preparing").length;
  const licensesInReview = bindingRows.filter(l => ["submitted", "under_review", "additional_info_requested"].includes(l.status)).length;

  const tabs = [
    { id: "licenses", label: "Licenses", icon: Shield, badge: bindingCount },
    // Machine-authored surface — restored per the 0.2.1.0 IA (licenses, eval,
    // fields, docs, templates); its inner strip carries the ai accent.
    { id: "evaluation", label: "AI Evaluation", icon: Sparkles, badge: evaluation ? visibleNudges(evaluation.nudges).length : null },
    { id: "fields", label: "Fields", icon: ClipboardList, badge: `${fieldsComplete}/${fieldsTotal}` },
    { id: "documents", label: "Documents", icon: FileText, badge: null },
    { id: "templates", label: "Templates", icon: FileOutput, badge: null },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">

      {/* ─── HERO: Readiness + Actions ─── */}
      <div className="flex items-start gap-5 mb-6">
        {/* Readiness ring */}
        <div className="flex flex-col items-center flex-shrink-0">
          <HealthRing percentage={readiness} size={72} strokeWidth={5} tooltip={`${readiness}% of required fields filled`} />
          <span className="text-[13px] text-text-tertiary mt-1">ready</span>
        </div>

        {/* Action cards — one-thing focus mode when top nudge is critical */}
        <div className="flex-1 min-w-0">
          <h2 className="text-[13px] font-medium text-text-tertiary uppercase tracking-wider mb-2">What to do next</h2>
          {/* Height-animated container — measures content and transitions smoothly */}
          <div
            style={{
              height: heroHeight != null ? `${heroHeight}px` : "auto",
              transition: "height 300ms cubic-bezier(0.4, 0, 0.2, 1)",
              overflow: "hidden",
            }}
          >
            <div ref={heroContentRef} className="space-y-2">
              {topActions.length > 0 ? (topActions[0]?.type === "critical_path" ? (
                /* Focus mode: show only the critical path nudge, bigger */
                <NudgeCard nudge={topActions[0]} variant="critical" />
              ) : topActions.map((nudge: any) => (
                <NudgeCard key={nudge.id} nudge={nudge} variant="default" />
              ))) : evaluationLoaded ? (
                /* Loaded but no actionable nudges — guide to first useful step */
                <div className="bg-surface-secondary border border-border rounded-lg px-3 py-3">
                  <div className="text-[14px] text-text-secondary mb-1.5">
                    {readiness >= 80
                      ? "You're almost there. Review your licenses to submit."
                      : readiness > 0
                        ? "Keep filling fields to unlock licensing steps."
                        : "Start by uploading a document — we'll extract fields automatically."}
                  </div>
                  <button
                    onClick={() => setActiveTab(readiness >= 80 ? "licenses" : readiness > 0 ? "fields" : "documents")}
                    className="text-[13px] text-brand font-medium hover:underline"
                  >
                    {readiness >= 80 ? "Review licenses →" : readiness > 0 ? "Go to fields →" : "Upload a document →"}
                  </button>
                </div>
              ) : (
                /* Loading: single small skeleton — no big snap when content arrives */
                <div className="h-14 bg-surface-tertiary rounded-lg animate-pulse" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── JOURNEY PHASES ─── */}
      <div className="flex items-center gap-0 mb-6 overflow-x-auto">
        {phases.map((p, i) => (
          <div key={i} className="flex items-center">
            <Tooltip text={p.description} position="bottom">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap ${
                i < phase ? "bg-brand-light text-brand" :
                i === phase ? "bg-brand text-on-solid" :
                "bg-surface-tertiary text-text-tertiary"
              }`}>
                {i < phase ? "✓" : i === phase ? "●" : "○"} {p.label}
              </div>
            </Tooltip>
            {i < phases.length - 1 && <div className="w-4 h-px bg-border mx-0.5" />}
          </div>
        ))}
      </div>

      {/* ─── PHASE-AWARE PROGRESS ─── */}
      <PhaseProgress
        phase={phase}
        fieldsComplete={fieldsComplete}
        fieldsTotal={fieldsTotal}
        licensesApproved={licensesApproved}
        licensesPreparing={licensesPreparing}
        licensesInReview={licensesInReview}
        licensesNotStarted={licensesNotStarted}
        licensesTotal={bindingCount}
        alerts={alerts}
        exportUrl={`/api/projects/${id}/report`}
      />

      {/* ─── TABS (simplified) ─── */}
      <Tabs
        items={tabs}
        active={activeTab}
        onChange={(id) => {
          setActiveTab(id);
          // Write-through so refresh/share/back restore the visible tab; a
          // stale ?field focus target does not survive a manual tab switch.
          const params = new URLSearchParams(searchParams);
          params.set("tab", id);
          params.delete("field");
          setSearchParams(params, { replace: true });
        }}
        label="Project sections"
        idBase="project-tabs"
        className="mb-4"
      />

      {/* Tab content — one mounted panel, labelled by its tab */}
      <div {...tabPanelProps("project-tabs", activeTab)}>
      {activeTab === "licenses" && <LicensesAndComplianceTab licenses={licenses} projectId={id!} countryCode={project.country_code} complianceSummary={complianceSummary} complianceItems={complianceItems} bankability={evaluation?.bankability} evaluationLoaded={evaluationLoaded} onComplianceUpdate={loadData} />}
      {activeTab === "evaluation" && <EvaluationPanel projectId={id!} evaluation={evaluation} evaluationLoaded={evaluationLoaded} onRetry={refreshEvaluation} />}
      {activeTab === "fields" && (
        <FieldsTab
          fields={fields}
          projectId={id!}
          licenses={licenses}
          focusFieldId={focusFieldId}
          onFocusConsumed={() => {
            const params = new URLSearchParams(searchParams);
            params.delete("field");
            setSearchParams(params, { replace: true });
          }}
          onFieldSaved={refreshEvaluation}
        />
      )}
      {activeTab === "documents" && <DocumentsTab projectId={id!} countryCode={project.country_code} onSyncComplete={() => { loadData(); refreshEvaluation(); }} />}
      {activeTab === "templates" && <TemplateList projectId={id!} countryCode={project.country_code} />}
      </div>
    </div>
  );
}

// ─── Licenses & Compliance Tab (merged) ───

function LicensesAndComplianceTab({ licenses, projectId, countryCode, complianceItems = [], bankability, evaluationLoaded = true, onComplianceUpdate }: {
  licenses: any[]; projectId: string; countryCode: string; complianceSummary?: any; complianceItems?: any[]; bankability?: any; evaluationLoaded?: boolean; onComplianceUpdate?: () => void;
}) {
  const [view, setView] = useState<"list" | "timeline">("list");

  if (licenses.length === 0) {
    return <p className="text-[16px] text-text-secondary py-4">No licenses assigned to this project.</p>;
  }

  // Parse prerequisite IDs
  const parsedLicenses = licenses.map(l => ({
    ...l,
    prerequisite_ids: tryParseJSON(l.prerequisite_license_ids, []),
  }));

  // DISPLAY partition on the engine's own verdicts. The list leads with what
  // BINDS this project; rows the engine excluded (not_applicable) or whose
  // licence TYPE was retired by the corpus sit in a collapsed section — shown
  // on demand, never dropped (nothing may vanish; a wrong exclusion must stay
  // findable). In-flight rows (submitted and beyond) always stay in the main
  // list even if excluded/retired later — mirrors reevaluateProjectApplicability's
  // in-flight protection.
  const inFlight = (l: any) => !["not_started", "preparing", "blocked"].includes(l.status);
  const setAside = parsedLicenses.filter(l => !inFlight(l) && (l.applicability === "not_applicable" || l.retired_reason));
  const bindingLicenses = parsedLicenses.filter(l => !setAside.includes(l));

  // Group by phase — the ONE shared catalogue (src/lib/workplan-phases.ts), the
  // same ids the Timeline view uses, so the two views can never disagree again.
  // (This view's private copy was missing 20 of the 30 NG corpus ids; they were
  // quietly falling into "Other" while the Timeline phased them.)
  const { phases: phaseGroups, other: ungroupedLicenses } = partitionByPhase(countryCode, bindingLicenses);

  return (
    <div className="space-y-4">
      {/* View toggle */}
      <div className="flex items-center justify-end gap-3 flex-wrap">

        {/* List / Timeline view toggle */}
        <Tabs
          variant="segmented"
          label="License view"
          items={[
            { id: "list", label: "List", icon: List },
            { id: "timeline", label: "Timeline", icon: Map },
          ]}
          active={view}
          onChange={setView}
        />
      </div>

      {/* View content */}
      {view === "timeline" ? (
        <WorkplanView licenses={licenses} projectId={projectId} countryCode={countryCode} />
      ) : (
        <>
          {phaseGroups.map(phase => {
            if (phase.licenses.length === 0) return null;
            return (
              <div key={phase.label}>
                <h3 className="text-[12px] font-medium text-text-tertiary uppercase tracking-wider mb-2">
                  {phase.label}
                </h3>
                <div className="space-y-1.5">
                  {phase.licenses.map(license => (
                    <LicenseRow key={license.id} license={license} allLicenses={parsedLicenses} projectId={projectId} />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Licences the phase catalogue has not placed yet — visible, never dropped */}
          {ungroupedLicenses.length > 0 && (
            <div>
              <h3 className="text-[12px] font-medium text-text-tertiary uppercase tracking-wider mb-2">Other</h3>
              <div className="space-y-1.5">
                {ungroupedLicenses.map(license => (
                  <LicenseRow key={license.id} license={license} allLicenses={parsedLicenses} projectId={projectId} />
                ))}
              </div>
            </div>
          )}

          {/* Rows the engine excluded for THIS project's answers, plus rows whose
              licence type the corpus retired — collapsed, with the reason, never
              hidden outright. */}
          {setAside.length > 0 && (
            <details className="group">
              <summary className="cursor-pointer text-[12px] font-medium text-text-tertiary uppercase tracking-wider mb-2 list-none flex items-center gap-1.5">
                <span className="inline-block transition-transform group-open:rotate-90">›</span>
                Not applicable to this project ({setAside.length})
              </summary>
              <div className="space-y-1.5 opacity-70">
                {setAside.map(license => (
                  <div key={license.id}>
                    <LicenseRow license={license} allLicenses={parsedLicenses} projectId={projectId} />
                    {(license.applicability_reason || license.retired_reason) && (
                      <p className="text-[11px] text-text-tertiary mt-0.5 ml-3">
                        {license.retired_reason ?? license.applicability_reason}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* ESG & Operational Compliance (collapsible, secondary) */}
          {complianceItems.length > 0 && (() => {
            const addressed = complianceItems.filter(i => i.status === "compliant" || i.status === "not_applicable" || i.status === "exempt").length;
            const pct = complianceItems.length > 0 ? Math.round((addressed / complianceItems.length) * 100) : 0;

            const tierGroups = [
              { tier: "gate", label: "Blocks licensing", color: "border-l-danger", items: complianceItems.filter(i => i.government_tier === "gate") },
              { tier: "pre_operational", label: "Required before construction", color: "border-l-warning", items: complianceItems.filter(i => i.government_tier === "pre_operational") },
              { tier: "ongoing", label: "Ongoing obligations", color: "border-l-info", items: complianceItems.filter(i => i.government_tier === "ongoing" || !i.government_tier) },
            ].filter(g => g.items.length > 0);

            return (
              <details className="mt-4 border border-border rounded-xl overflow-hidden group">
                <summary className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-surface-secondary/50 transition-colors cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                  <ShieldCheck size={15} className={pct === 100 ? "text-brand" : "text-text-tertiary"} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] font-medium">ESG & Operational Compliance</span>
                      <span className="text-[12px] text-text-tertiary">{addressed}/{complianceItems.length}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-1 rounded-full bg-surface-tertiary overflow-hidden max-w-[120px]">
                        <div className="h-full bg-brand rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[11px] text-text-tertiary">{pct}%</span>
                    </div>
                  </div>
                  <ChevronRight size={14} className="text-text-tertiary transition-transform group-open:rotate-90" />
                </summary>
                <div className="px-4 pb-4 pt-2 border-t border-border space-y-4">
                  <p className="text-[12px] text-text-tertiary">
                    {pct === 100 ? "All ESG and operational requirements addressed." :
                     pct > 0 ? `${addressed} of ${complianceItems.length} addressed. Review remaining items before submission.` :
                     "Environmental, social, and governance requirements that accompany your licensing journey. Address these as you progress."}
                  </p>
                  {tierGroups.map(group => (
                    <div key={group.tier}>
                      <h4 className="text-[11px] font-medium text-text-tertiary uppercase tracking-wider mb-1.5">{group.label}</h4>
                      <div className="space-y-1">
                        {group.items.map((item: any) => (
                          <ComplianceItemRow key={item.id} item={item} projectId={projectId} onUpdate={onComplianceUpdate} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            );
          })()}

          {/* Bankability teaser — hidden by feature flag (#16) */}
          {SHOW_BANKABILITY && !evaluationLoaded && (
            <div className="mt-4 border border-border rounded-xl bg-surface-secondary/50 p-4">
              <div className="skeleton h-3 w-28 mb-3" />
              <div className="skeleton h-4 w-64 mb-2" />
              <div className="flex items-center gap-3 mt-3">
                <div className="skeleton w-8 h-6 rounded" />
                <div className="flex-1 skeleton h-1.5 rounded-full" />
                <div className="skeleton h-3 w-8" />
              </div>
            </div>
          )}
          {SHOW_BANKABILITY && bankability && (
            <div className="mt-6 border border-border rounded-xl bg-surface-secondary/50 p-4">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-[13px] font-medium text-text-tertiary uppercase tracking-wider">
                  Investor readiness
                </h3>
                <span className="text-[12px] text-text-tertiary">Coming soon</span>
              </div>
              <p className="text-[13px] text-text-secondary mb-3">
                How bankable is this project? Once your core license data is in place, we'll score your project across financial, technical, and regulatory dimensions that investors care about.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className={`text-[18px] font-bold ${bandText(band(bankability.overall_score, "bankability"))}`}>
                    {bankabilityGrade(band(bankability.overall_score, "bankability"))}
                  </div>
                  <span className="text-[12px] text-text-tertiary">grade</span>
                </div>
                <div className="flex-1 h-1.5 rounded-full bg-surface-tertiary overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      bandBar(band(bankability.overall_score, "bankability"))
                    }`}
                    style={{ width: `${bankability.overall_score ?? 0}%` }}
                  />
                </div>
                <span className="text-[12px] text-text-tertiary">{bankability.overall_score ?? 0}%</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ComplianceItemRow({ item, projectId, onUpdate }: { item: any; projectId: string; onUpdate?: () => void }) {
  const [updating, setUpdating] = useState(false);
  const statusConfig = COMPLIANCE_STATUS_CONFIG[item.status as keyof typeof COMPLIANCE_STATUS_CONFIG] ?? COMPLIANCE_STATUS_CONFIG.not_assessed;
  const tierColor = item.government_tier === "gate" ? "border-l-danger" :
                    item.government_tier === "pre_operational" ? "border-l-warning" : "border-l-info";

  async function handleStatusChange(newStatus: string) {
    setUpdating(true);
    try {
      await api.updateProjectCompliance(projectId, item.country_compliance_id, newStatus);
      onUpdate?.();
    } catch {} finally {
      setUpdating(false);
    }
  }

  return (
    <div className={`flex items-center gap-3 px-3 py-2 border border-border rounded-lg border-l-3 ${tierColor}`}>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium truncate">{item.display_name}</div>
        {item.approval_body && <div className="text-[11px] text-text-tertiary truncate">{item.approval_body}</div>}
      </div>
      <select
        value={item.status}
        onChange={(e) => handleStatusChange(e.target.value)}
        disabled={updating}
        className={`text-[11px] px-2 py-1 rounded-lg border-0 cursor-pointer ${statusConfig.bg} ${statusConfig.text} disabled:opacity-50`}
      >
        <option value="not_assessed">Not assessed</option>
        <option value="in_progress">In progress</option>
        <option value="compliant">Compliant</option>
        <option value="non_compliant">Non-compliant</option>
        <option value="not_applicable">N/A</option>
        <option value="exempt">Exempt</option>
      </select>
    </div>
  );
}

function LicenseRow({ license, allLicenses, projectId }: { license: any; allLicenses: any[]; projectId: string }) {
  // Determine if blocked by prerequisites
  const prereqs: string[] = license.prerequisite_ids;
  const unmetPrereqs = prereqs
    .map(preId => allLicenses.find((l: any) => l.license_type_id === preId))
    .filter((pre: any) => !pre || pre.status !== "approved");
  const isBlocked = unmetPrereqs.length > 0;
  const displayStatus = isBlocked && license.status === "not_started" ? "blocked" : license.status;
  const blockerNames = unmetPrereqs
    .filter((pre: any) => pre)
    .map((pre: any) => pre.license_name);

  return (
    <Link
      to={`/projects/${projectId}/licenses/${license.license_type_id}`}
      className="flex items-center gap-3 px-3 py-2.5 bg-surface border border-border rounded-xl card-interactive hover:border-border-secondary no-underline text-text-primary"
    >
      <div className="flex-1 min-w-0">
        <div className="text-[16px] font-medium truncate">{license.license_name}</div>
        <div className="text-[13px] text-text-tertiary truncate">
          {displayStatus === "blocked" && blockerNames.length > 0 ? (
            <span className="text-warning-text">
              Blocked by {blockerNames.slice(0, 2).join(", ")}{blockerNames.length > 2 ? `, +${blockerNames.length - 2}` : ""}
            </span>
          ) : (
            license.issuing_authority
          )}
        </div>
      </div>
      {license.statutory_timeline_days && (
        <Tooltip text="Estimated regulatory processing time">
          <span className="text-[13px] text-text-tertiary whitespace-nowrap">
            ~{Math.round(license.statutory_timeline_days / 30)}mo
          </span>
        </Tooltip>
      )}
      <StatusPill status={displayStatus} />
      <ChevronRight size={14} className="text-text-tertiary flex-shrink-0" />
    </Link>
  );
}

// ─── Fields Tab ───

type FieldSort = "priority" | "updated" | "name";
type FieldStatusFilter = "all" | "missing" | "extracted" | "manual" | "calculated";

function getFieldStatus(f: any): FieldStatusFilter {
  if (!f.value && f.value !== 0) return "missing";
  return f.source_method ?? "manual";
}

const STATUS_FILTER_OPTIONS: { value: FieldStatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "missing", label: "Missing" },
  { value: "extracted", label: "Extracted" },
  { value: "manual", label: "Manual" },
  { value: "calculated", label: "Calculated" },
];

const SORT_OPTIONS: { value: FieldSort; label: string }[] = [
  { value: "priority", label: "Priority" },
  { value: "updated", label: "Recently updated" },
  { value: "name", label: "Name" },
];

function FieldsTab({ fields, projectId, licenses, focusFieldId, onFocusConsumed, onFieldSaved }: { fields: any[]; projectId: string; licenses: any[]; focusFieldId?: string | null; onFocusConsumed?: () => void; onFieldSaved?: () => void }) {
  // Build a set of doc types that are mandatory for at least one active license
  const requiredDocTypes = (() => {
    const set = new Set<string>();
    const activeLicenses = licenses.filter(l => l.status !== "approved" && l.status !== "renewed");
    for (const lic of activeLicenses) {
      const docTypes: string[] = tryParseJSON(lic.required_doc_types, []);
      for (const dt of docTypes) set.add(dt);
    }
    return set;
  })();
  const [fieldData, setFieldData] = useState(fields);
  const [sortBy, setSortBy] = useState<FieldSort>("priority");
  const [statusFilter, setStatusFilter] = useState<FieldStatusFilter>("all");

  // Sync with parent when props change
  useEffect(() => { setFieldData(fields); }, [fields]);

  // Scroll to + highlight a focused field (from ?field= URL param)
  useEffect(() => {
    if (!focusFieldId) return;
    // Wait a tick for the DOM to render
    const t = setTimeout(() => {
      const el = document.getElementById(`field-${focusFieldId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-brand", "rounded-xl");
        setTimeout(() => el.classList.remove("ring-2", "ring-brand", "rounded-xl"), 2500);
      }
      onFocusConsumed?.();
    }, 100);
    return () => clearTimeout(t);
  }, [focusFieldId]);

  // Compute impact score per field: how many active licenses reference it
  // Active = not yet approved (still needs the field to progress)
  const fieldImpact = (() => {
    const scores: Record<string, { count: number; licenses: string[] }> = {};
    const activeLicenses = licenses.filter(l => l.status !== "approved" && l.status !== "renewed");
    for (const lic of activeLicenses) {
      const requiredIds: string[] = tryParseJSON(lic.required_field_ids, []);
      for (const fid of requiredIds) {
        if (!scores[fid]) scores[fid] = { count: 0, licenses: [] };
        scores[fid]!.count++;
        scores[fid]!.licenses.push(lic.license_name);
      }
    }
    return scores;
  })();

  // Filter fields by status
  const filteredData = statusFilter === "all"
    ? fieldData
    : fieldData.filter(f => getFieldStatus(f) === statusFilter);

  // Count fields per status for filter badges
  const statusCounts = fieldData.reduce((acc, f) => {
    const s = getFieldStatus(f);
    acc[s] = (acc[s] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Top 3 missing fields ranked by impact (only in priority sort, no filter active)
  const showPriority = sortBy === "priority" && statusFilter === "all";
  const topPriority = showPriority
    ? filteredData
        .filter(f => !f.value || f.value === "")
        .map(f => ({ field: f, impact: fieldImpact[f.field_id]?.count ?? 0, licenses: fieldImpact[f.field_id]?.licenses ?? [] }))
        .filter(x => x.impact > 0)
        .sort((a, b) => b.impact - a.impact)
        .slice(0, 3)
    : [];
  const topPriorityIds = new Set(topPriority.map(t => t.field.field_id));

  // Sort + group logic depends on sort mode
  const sortedContent = (() => {
    if (sortBy === "priority") {
      // Group by category, sorted by most-missing-with-impact first
      const grouped = FIELD_CATEGORIES
        .map(cat => {
          const catFields = filteredData
            .filter(f => f.category === cat.id && !topPriorityIds.has(f.field_id));
          const missingImpact = catFields.reduce((sum, f) =>
            sum + ((f.value || f.value === "") ? 0 : (fieldImpact[f.field_id]?.count ?? 0)), 0);
          return { ...cat, fields: catFields, missingImpact };
        })
        .filter(g => g.fields.length > 0)
        .sort((a, b) => b.missingImpact - a.missingImpact);
      return { mode: "grouped" as const, groups: grouped };
    }

    // Flat list for other sorts
    const sorted = [...filteredData].sort((a, b) => {
      if (sortBy === "updated") {
        const aTime = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const bTime = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return bTime - aTime;
      }
      // name
      return (a.display_label ?? a.name ?? "").localeCompare(b.display_label ?? b.name ?? "");
    });
    return { mode: "flat" as const, fields: sorted };
  })();

  function handleFieldUpdate(fieldId: string, newValue: string | null) {
    setFieldData(prev => prev.map(f =>
      f.field_id === fieldId ? { ...f, value: newValue, source_method: "manual" } : f
    ));
    onFieldSaved?.();
  }

  return (
    <div className="space-y-4">
      {/* Sort & Filter controls */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Sort */}
        <div className="flex items-center gap-1.5">
          <ArrowUpDown size={12} className="text-text-tertiary" />
          {SORT_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setSortBy(opt.value)}
              className={`px-2.5 py-1 rounded-lg text-[12px] font-medium transition-colors ${
                sortBy === opt.value
                  ? "bg-brand/15 text-brand"
                  : "text-text-tertiary hover:text-text-secondary hover:bg-surface-2"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="w-px h-4 bg-border" />

        {/* Filter */}
        <div className="flex items-center gap-1.5">
          <Filter size={12} className="text-text-tertiary" />
          {STATUS_FILTER_OPTIONS.map(opt => {
            const count = opt.value === "all" ? fieldData.length : (statusCounts[opt.value] ?? 0);
            if (opt.value !== "all" && count === 0) return null;
            return (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={`px-2.5 py-1 rounded-lg text-[12px] font-medium transition-colors ${
                  statusFilter === opt.value
                    ? "bg-brand/15 text-brand"
                    : "text-text-tertiary hover:text-text-secondary hover:bg-surface-2"
                }`}
              >
                {opt.label}
                <span className="ml-1 opacity-60">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Priority fields — top 3 most-impactful missing (only in priority sort) */}
      {topPriority.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[12px] font-medium text-brand uppercase tracking-wider flex items-center gap-1.5">
              <AlertTriangle size={12} /> Priority
            </h3>
            <span className="text-[12px] text-text-tertiary">Fills the most licenses</span>
          </div>
          <div className="space-y-1.5">
            {topPriority.map(({ field, impact, licenses: licNames }) => (
              <div key={field.field_id} id={`field-${field.field_id}`}>
                <FieldCard
                  field={field}
                  projectId={projectId}
                  onUpdate={handleFieldUpdate}
                  docIsRequired={!!field.source_doc_type && requiredDocTypes.has(field.source_doc_type)}
                />
                <p className="text-[11px] text-text-tertiary mt-0.5 ml-3">
                  Required by {impact} license{impact !== 1 ? "s" : ""}: {licNames.slice(0, 2).join(", ")}{licNames.length > 2 ? `, +${licNames.length - 2}` : ""}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fields list */}
      {sortedContent.mode === "grouped" ? (
        // Priority sort: grouped by category
        sortedContent.groups.map(group => {
          const complete = group.fields.filter(f => f.value != null && f.value !== "").length;
          return (
            <div key={group.id}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[12px] font-medium text-text-tertiary uppercase tracking-wider">
                  {group.label}
                </h3>
                <span className={`text-[12px] font-medium ${
                  complete === group.fields.length ? "text-brand" : "text-text-tertiary"
                }`}>
                  {complete}/{group.fields.length}
                </span>
              </div>
              <div className="space-y-1.5">
                {group.fields.map((field: any) => (
                  <div key={field.field_id} id={`field-${field.field_id}`}>
                    <FieldCard
                      field={field}
                      projectId={projectId}
                      onUpdate={handleFieldUpdate}
                      docIsRequired={!!field.source_doc_type && requiredDocTypes.has(field.source_doc_type)}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })
      ) : (
        // Flat list for updated/name sort
        <div className="space-y-1.5">
          {sortedContent.fields.map((field: any) => (
            <div key={field.field_id} id={`field-${field.field_id}`}>
              <FieldCard
                field={field}
                projectId={projectId}
                onUpdate={handleFieldUpdate}
                docIsRequired={!!field.source_doc_type && requiredDocTypes.has(field.source_doc_type)}
              />
            </div>
          ))}
        </div>
      )}

      {filteredData.length === 0 && (
        <p className="text-[13px] text-text-tertiary text-center py-8">
          No fields match this filter.
        </p>
      )}
    </div>
  );
}

// ─── Documents Tab ───

function DocumentsTab({ projectId, countryCode, onSyncComplete }: { projectId: string; countryCode: string; onSyncComplete?: () => void }) {
  const [docs, setDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewDoc, setPreviewDoc] = useState<any | null>(null);
  const [deleteDoc, setDeleteDoc] = useState<any | null>(null);

  function loadDocs() {
    api.getProjectDocuments(projectId).then(d => {
      setDocs(d);
      setLoading(false);
    }).catch(() => setLoading(false));
  }

  useEffect(() => { loadDocs(); }, [projectId]);

  return (
    <div className="space-y-5">
      {/* Cloud drive connection */}
      <CloudDriveConnect projectId={projectId} onSyncComplete={() => { loadDocs(); onSyncComplete?.(); }} />

      {/* Batch upload (files or folders) + single-file extraction */}
      <BatchUploadFlow
        projectId={projectId}
        countryCode={countryCode}
        onComplete={() => { loadDocs(); onSyncComplete?.(); }}
      />

      {/* Uploaded documents list */}
      {loading ? (
        <div className="text-[16px] text-text-secondary py-2">Loading documents...</div>
      ) : docs.length > 0 && (
        <div>
          <h3 className="text-[12px] font-medium text-text-tertiary uppercase tracking-wider mb-2">
            Uploaded documents
          </h3>
          <div className="space-y-1.5">
            {docs.map(doc => {
              const isStuck = doc.extraction_status === "processing";
              const isFailed = doc.extraction_status === "failed";
              const canRetry = isStuck || isFailed;
              return (
                <div
                  key={doc.id}
                  onClick={() => setPreviewDoc(doc)}
                  className="flex items-center gap-3 px-3 py-2.5 bg-surface border border-border rounded-xl cursor-pointer hover:border-brand/30 transition-colors group"
                >
                  <FileText size={16} className="text-text-tertiary" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[16px] font-medium truncate">{doc.display_name}</div>
                    <div className="text-[13px] text-text-tertiary">{doc.file_name} · {doc.doc_type.replace(/_/g, " ")}</div>
                  </div>
                  <Eye size={14} className="text-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  {canRetry && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        const btn = e.currentTarget;
                        btn.textContent = "Extracting...";
                        btn.disabled = true;
                        try {
                          await api.retryExtraction(projectId, doc.id);
                          await api.extractDocument(projectId, doc.id);
                        } catch { /* extraction may fail — status will reflect it */ }
                        loadDocs();
                      }}
                      className="text-[12px] text-warning-text hover:text-warning-text/80 flex items-center gap-1 disabled:opacity-50"
                      title="Retry extraction"
                    >
                      <RotateCcw size={12} /> Retry
                    </button>
                  )}
                  <StatusPill status={doc.doc_type === "unclassified" ? "unrecognized" : doc.extraction_status === "completed" ? "approved" : doc.extraction_status === "processing" ? "preparing" : doc.extraction_status === "failed" ? "rejected" : "not_started"} />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteDoc(doc);
                    }}
                    className="text-text-tertiary hover:text-danger transition-colors"
                    title="Delete document"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Delete confirmation — the one dialog, never the native confirm() */}
      {deleteDoc && (
        <ConfirmDialog
          title="Delete document"
          body={<>This permanently removes <span className="font-medium text-text-primary">{deleteDoc.display_name}</span> from the project.</>}
          confirmLabel="Delete"
          tone="danger"
          onConfirm={async () => {
            await api.deleteDocument(projectId, deleteDoc.id);
            loadDocs();
          }}
          onClose={() => setDeleteDoc(null)}
        />
      )}

      {/* Document preview modal */}
      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc}
          projectId={projectId}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
}

// ─── Phase-aware Progress Summary ───

// ─── Nudge Card (clickable as a whole, navigates to nudge.action_href) ───

function NudgeCard({ nudge, variant }: { nudge: any; variant: "critical" | "default" }) {
  const colors: Record<string, string> = {
    critical_path: "border-l-danger bg-danger-light/20 hover:bg-danger-light/30",
    gap_quick_win: "border-l-brand bg-brand-light/20 hover:bg-brand-light/30",
    gap: "border-l-warning bg-warning-light/20 hover:bg-warning-light/30",
    optimization: "border-l-info bg-info-light/20 hover:bg-info-light/30",
    regulatory_risk: "border-l-danger bg-danger-light/20 hover:bg-danger-light/30",
    bankability: "border-l-ai bg-ai-light/20 hover:bg-ai-light/30",
  };

  if (variant === "critical") {
    const inner = (
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-medium text-danger uppercase tracking-wider mb-1">Critical path</div>
          <div className="text-[17px] font-semibold mb-1">{nudge.title}</div>
          <div className="text-[14px] text-text-secondary leading-relaxed">{nudge.body}</div>
        </div>
        <div className="px-3 py-1.5 bg-danger text-on-solid rounded-lg text-[13px] font-medium whitespace-nowrap flex-shrink-0">
          {nudge.action_label || "Resolve"} →
        </div>
      </div>
    );
    if (nudge.action_href) {
      return (
        <Link to={nudge.action_href} className="animate-fade-in block border-l-[4px] border-l-danger bg-danger-light/20 hover:bg-danger-light/30 rounded-r-xl px-4 py-3 no-underline text-text-primary transition-colors">
          {inner}
        </Link>
      );
    }
    return <div className="animate-fade-in border-l-[4px] border-l-danger bg-danger-light/20 rounded-r-xl px-4 py-3">{inner}</div>;
  }

  const inner = (
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-medium">{nudge.title}</div>
        <div className="text-[13px] text-text-secondary mt-0.5 line-clamp-2">{nudge.body}</div>
      </div>
      {nudge.action_label && (
        <span className="text-[12px] text-brand font-medium whitespace-nowrap flex-shrink-0">
          {nudge.action_label} →
        </span>
      )}
    </div>
  );

  if (nudge.action_href) {
    return (
      <Link
        to={nudge.action_href}
        className={`animate-fade-in block border-l-[3px] ${colors[nudge.type] || "border-l-border bg-surface-secondary hover:bg-surface-tertiary"} rounded-r-lg px-3 py-2 no-underline text-text-primary transition-colors`}
      >
        {inner}
      </Link>
    );
  }

  return (
    <div className={`animate-fade-in border-l-[3px] ${colors[nudge.type] || "border-l-border bg-surface-secondary"} rounded-r-lg px-3 py-2`}>
      {inner}
    </div>
  );
}

function PhaseProgress({ phase, fieldsComplete, fieldsTotal, licensesApproved, licensesPreparing, licensesInReview, licensesNotStarted, licensesTotal, alerts, exportUrl }: {
  phase: number;
  fieldsComplete: number;
  fieldsTotal: number;
  licensesApproved: number;
  licensesPreparing: number;
  licensesInReview: number;
  licensesNotStarted: number;
  licensesTotal: number;
  alerts: any[];
  exportUrl: string;
}) {
  // Build actionable message based on current phase
  let actionText: React.ReactNode;
  let progressText: string;

  switch (phase) {
    case 0: { // Data Collection
      const fieldsRemaining = fieldsTotal - fieldsComplete;
      actionText = fieldsRemaining > 0
        ? <><span className="text-text-primary font-medium">{fieldsRemaining} fields</span> still needed to start preparing licenses</>
        : <>Fields collected. Move on to preparing your first license application.</>;
      progressText = `${fieldsComplete}/${fieldsTotal} fields filled`;
      break;
    }
    case 1: { // License Preparation
      const readyToSubmit = licensesPreparing;
      const needData = licensesNotStarted;
      const parts: string[] = [];
      if (readyToSubmit > 0) parts.push(`${readyToSubmit} license${readyToSubmit !== 1 ? "s" : ""} being prepared`);
      if (needData > 0) parts.push(`${needData} still need data`);
      if (licensesApproved > 0) parts.push(`${licensesApproved} already approved`);
      actionText = parts.length > 0
        ? <><span className="text-text-primary font-medium">{parts[0]}</span>{parts.length > 1 ? ` · ${parts.slice(1).join(" · ")}` : ""}</>
        : <>Preparing license applications</>;
      progressText = `${licensesApproved}/${licensesTotal} licenses secured`;
      break;
    }
    case 2: { // Submission & Tracking
      const parts: string[] = [];
      if (licensesInReview > 0) parts.push(`${licensesInReview} submitted, awaiting decision`);
      if (licensesPreparing > 0) parts.push(`${licensesPreparing} still preparing`);
      const remaining = licensesTotal - licensesApproved;
      actionText = parts.length > 0
        ? <><span className="text-text-primary font-medium">{parts[0]}</span>{parts.length > 1 ? ` · ${parts.slice(1).join(" · ")}` : ""}</>
        : <>{remaining} license{remaining !== 1 ? "s" : ""} remaining</>;
      progressText = `${licensesApproved}/${licensesTotal} approved`;
      break;
    }
    case 3: // Operations
    default: {
      actionText = <>All licenses secured. Monitor compliance and renewals.</>;
      progressText = `${licensesApproved}/${licensesTotal} approved`;
      break;
    }
  }

  return (
    <div className="flex items-center gap-3 mb-5 text-[13px] flex-wrap">
      {/* Actionable message */}
      <div className="flex items-center gap-1.5 text-text-secondary flex-1 min-w-0">
        {phase < 3 ? (
          <ArrowRight size={12} className="text-brand flex-shrink-0" />
        ) : (
          <CheckCircle size={12} className="text-brand flex-shrink-0" />
        )}
        <span className="truncate">{actionText}</span>
      </div>

      {/* Light progress reference */}
      <span className="text-text-tertiary whitespace-nowrap">{progressText}</span>

      {/* Alerts */}
      {alerts.length > 0 && (
        <>
          <span className="text-text-tertiary">·</span>
          <span className="text-danger flex items-center gap-1 whitespace-nowrap">
            <AlertTriangle size={11} />
            {alerts.length} alert{alerts.length !== 1 ? "s" : ""}
          </span>
        </>
      )}

      {/* Export */}
      <a
        href={exportUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[12px] text-text-tertiary hover:text-text-secondary no-underline whitespace-nowrap"
      >
        <Download size={11} className="inline mr-1" />Export
      </a>
    </div>
  );
}

// ─── Helpers ───

import { tryParseJSON } from "../lib/utils.ts";
import { partitionByPhase } from "../lib/workplan-phases.ts";
